import reactUpdate from 'immutability-helper';
import {useMemo, useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {buildReactionFromComponents, BuildReactionFromComponentsResponse} from 'chemistry/ChemistryDAO';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (const evaluatedRow of computedTable) {
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (moleculeId && (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)) {
      components[role].push(moleculeId);
    }
  }

  return components;
}

function buildUpdatedReactionDiagram(
  components: SyncedReactionComponents
): Promise<BuildReactionFromComponentsResponse> {
  return buildReactionFromComponents(
    components[ChemistryReactionRole.REACTANT],
    components[ChemistryReactionRole.PRODUCT]
  );
}

/**
 * Syncs reaction components from a stoichiometry table to a reaction scheme file.
 *
 * Monitors reactant and product molecules in the table and automatically updates
 * the reaction scheme when components change. Calls the chemistry service to build
 * a new reaction diagram and invokes `onUpdate` with both the updated NoteLinkedFile
 * (carrying the new external file) and the updated table (with refreshed syncedComponents).
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when a new diagram is generated
 * @returns true if a diagram update is in progress, false otherwise
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): boolean {
  const diagramReactionComponents = structuredTable.metadata.syncedComponents;
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns),
    [computedTable, structuredTable.columns]
  );
  const {currentState: reactionDiagramUpdateResponse, makeCall: callBuildUpdatedReactionDiagram} =
    useAsyncState({makeCall: buildUpdatedReactionDiagram, keepOnlyLatest: true, includeInput: true});

  useEffect(() => {
    if (
      diagramReactionComponents == null ||
      // Use deep equality check since the tableReactionComponents array is not completely stable.
      !isEqual(tableReactionComponents, diagramReactionComponents)
    ) {
      callBuildUpdatedReactionDiagram(tableReactionComponents);
    }
  }, [tableReactionComponents, diagramReactionComponents, callBuildUpdatedReactionDiagram]);

  const updatedReactionDiagramExternalFile =
    reactionDiagramUpdateResponse.status === AsyncStatus.SUCCESS
      ? reactionDiagramUpdateResponse.data.externalFile
      : null;
  const updatedReactionInput =
    reactionDiagramUpdateResponse.status !== AsyncStatus.NOT_FETCHED
      ? reactionDiagramUpdateResponse.input![0]
      : null;

  const updatedReactionDiagramNoteLinkedFile =
    reactionDiagramUpdateResponse.status === AsyncStatus.SUCCESS
      ? reactionDiagramUpdateResponse.data.noteLinkedFile
      : null;

  useEffect(() => {
    if (
      updatedReactionDiagramExternalFile != null &&
      // Ensure that this is not a stale file that no longer matches because the reaction was updated via sketcher.
      isEqual(updatedReactionInput, tableReactionComponents)
    ) {
      let updatedFile: NoteLinkedFile;
      if (reactionSchemeFile.externalFile != null) {
        // Reaction scheme file is server-backed: update the externalFile in place.
        if (updatedReactionDiagramExternalFile.id === reactionSchemeFile.externalFile.id) {
          return;
        }
        updatedFile = reactUpdate(reactionSchemeFile, {
          externalFile: {$set: updatedReactionDiagramExternalFile},
        });
      } else {
        // Reaction scheme file is a placeholder (no drawing yet): use the server-created
        // NoteLinkedFile from the API response to create a new server-backed file.
        // This ensures we never persist placeholder files via the insecure patcher path.
        if (updatedReactionDiagramNoteLinkedFile == null) {
          return;
        }
        updatedFile = serverNoteLinkedFileToClientNoteLinkedFile(updatedReactionDiagramNoteLinkedFile);
      }
      onUpdate(
        updatedFile,
        reactUpdate(structuredTable, {
          metadata: {
            $merge: {syncedComponents: tableReactionComponents},
          },
        })
      );
    }
  }, [
    updatedReactionDiagramExternalFile,
    updatedReactionDiagramNoteLinkedFile,
    structuredTable,
    tableReactionComponents,
    updatedReactionInput,
    onUpdate,
    reactionSchemeFile,
  ]);

  return reactionDiagramUpdateResponse.status === AsyncStatus.LOADING;
}
